import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { SocialLinks } from '../components/SocialLinks';
import { ServicesHighlight } from '../components/ServicesHighlight';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const { login, register, loginWithSSO } = useAuth();
  const navigate = useNavigate();
  const ssoExchangeStarted = useRef(false);

  // Check for an opaque SSO exchange code in URL (CIPHER-SEC-000A-P1-R3:
  // the backend never puts tokens directly in the URL; it hands back a
  // one-time code that must be exchanged server-side).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoCode = urlParams.get('sso_code');
    const ssoError = urlParams.get('sso_error') || urlParams.get('error');

    if (ssoCode) {
      if (ssoExchangeStarted.current) return;
      ssoExchangeStarted.current = true;
      // Scrub the one-time code from the URL immediately so it can't be
      // reused/resubmitted on reload or back/forward navigation.
      window.history.replaceState({}, '', '/login');

      api.post('/auth/sso/exchange', { code: ssoCode })
        .then((res) => {
          if (res.data.mfa_required) {
            // Reuse the existing password-login MFA challenge UI/flow.
            setMfaRequired(true);
            setMfaMethod(res.data.mfa_method);
            setMfaToken(res.data.mfa_token);
            return;
          }
          localStorage.setItem('token', res.data.access_token);
          loginWithSSO()
            .then(() => {
              toast.success('Signed in with Microsoft!');
              window.history.replaceState({}, '', '/dashboard');
              navigate('/dashboard');
            })
            .catch(() => {
              toast.error('Failed to get user info. Please try again.');
              localStorage.removeItem('token');
            });
        })
        .catch((err) => {
          toast.error(err.response?.data?.detail || 'Microsoft sign-in link is invalid or has expired. Please try signing in with Microsoft again.');
        });
    } else if (ssoError) {
      const errorMessages = {
        'sso_disabled': 'Microsoft sign-in is temporarily unavailable. Please use your email and password.',
        'no_account': 'No CipherOne account is linked to this Microsoft identity. Contact your administrator.',
        'account_inactive': 'Your account is inactive. Contact your administrator.',
        'use_portal': 'Customer accounts should use the customer portal.',
        'unauthorized_role': 'Your account does not have staff access. Contact your administrator.',
      };
      toast.error(errorMessages[ssoError] || `Microsoft sign-in failed: ${ssoError}`);
      window.history.replaceState({}, '', '/login');
    }
  }, [navigate, loginWithSSO]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await api.post('/auth/login', { email, password });
        if (res.data.mfa_required) {
          setMfaRequired(true);
          setMfaMethod(res.data.mfa_method);
          setMfaEmail(res.data.email);
          setMfaToken(res.data.mfa_token);
          if (res.data.mfa_method === 'sms') {
            await api.post('/mfa/send-login-code', {}, { headers: { Authorization: `Bearer ${res.data.mfa_token}` } });
            toast.info('Verification code sent to your phone');
          }
          setLoading(false);
          return;
        }
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        window.location.href = '/dashboard';
      } else {
        await register(email, password, fullName);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/mfa/verify-login', { code: mfaCode }, { headers: { Authorization: `Bearer ${mfaToken}` } });
      if (res.data.verified) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        if (res.data.backup_code_used) toast.info('Backup code used. You have fewer backup codes remaining.');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    }
    setLoading(false);
  };

  return (
    <div className="co-scope min-h-screen flex flex-col items-center justify-center relative overflow-x-hidden px-4 py-12 gap-10" style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #0a2233 0%, #041019 55%, #020b11 100%)' }}>
      {/* Restrained static grid texture (no animation) */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: 'linear-gradient(rgba(48,172,226,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(48,172,226,0.5) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />

      <Card className="w-full max-w-md relative z-10 bg-[#081a25] border border-[#193b4b] shadow-2xl shadow-black/50" data-testid="login-card">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex flex-col items-center gap-3">
            <img src="/cipherone-logo.png" alt="CipherOne" className="w-16 h-16 object-contain" />
            <div className="text-center">
              <CardTitle className="text-2xl font-bold tracking-tight text-[#f4f8fa]">
                CipherOne <span className="text-[#37c9ee]">MSP Portal</span>
              </CardTitle>
              <CardDescription className="text-[#b1c3ce] mt-1">
                {isLogin ? 'Sign in to your account' : 'Create a new account'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {mfaRequired ? (
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="text-center mb-4">
                <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(48,172,226,0.16)' }}>
                  <svg className="h-6 w-6 text-[#37c9ee]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </div>
                <p className="text-sm text-[#b1c3ce]">
                  {mfaMethod === 'sms' ? 'Enter the code sent to your phone' : 'Enter the code from your authenticator app'}
                </p>
              </div>
              <Input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="000000"
                maxLength={8}
                className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                autoFocus
                data-testid="mfa-code-input"
              />
              <Button type="submit" className="w-full bg-[#37c9ee] hover:bg-[#30ace2] text-[#041019] font-semibold h-11" disabled={loading || mfaCode.length < 6} data-testid="mfa-verify-btn">
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              <p className="text-xs text-center text-[#b1c3ce]">You can also use a backup code</p>
              <button type="button" className="w-full text-xs text-[#37c9ee] hover:underline" onClick={() => { setMfaRequired(false); setMfaCode(''); }}>
                Back to login
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  data-testid="fullName-input"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required={!isLogin}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    className="text-xs text-[#37c9ee] hover:underline"
                    onClick={() => navigate('/forgot-password')}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-button"
              className="w-full bg-[#37c9ee] hover:bg-[#30ace2] text-[#041019] font-semibold h-11"
              disabled={loading}
            >
              {loading ? 'Loading...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>

            {/* Microsoft SSO Option */}
            {isLogin && (
              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#193b4b]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#081a25] px-2 text-[#b1c3ce]">
                      Or continue with
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-4"
                  onClick={async () => {
                    try {
                      const currentOrigin = window.location.origin;
                      const response = await fetch(`/api/auth/microsoft?from=${encodeURIComponent(currentOrigin)}`);
                      const data = await response.json();
                      if (!response.ok || !data.authorization_url) {
                        toast.error(data.detail || 'Microsoft sign-in is temporarily unavailable. Please use your email and password.');
                        return;
                      }
                      window.location.href = data.authorization_url;
                    } catch (error) {
                      toast.error('Microsoft sign-in is temporarily unavailable. Please use your email and password.');
                    }
                  }}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  Sign in with Microsoft
                </Button>
              </div>
            )}
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              data-testid="toggle-mode-button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#37c9ee] hover:underline"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Public services lineup (home + business), only on the sign-in view */}
      {!mfaRequired && <ServicesHighlight />}

      {/* Bottom attribution */}
      <div className="z-10 flex flex-col items-center gap-2 w-full px-4">
        <SocialLinks tone="dark" align="center" />
        <p className="text-xs text-[#b1c3ce] font-mono tracking-wider opacity-50">
          ENCRYPTED &bull; SECURE &bull; MANAGED
        </p>
      </div>
    </div>
  );
}
